import errorHandler from "common/errorHandler";
import { call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import * as actions from "./actions";

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

function* getJdbcConnections(api) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/jdbc`);
    return result.data;
}

function* createJdbcConnection(api, connection) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/jdbc`, connection);
    return result.data;
}

function* getJdbcConnection(api, connectionId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}`);
        return result.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateJdbcConnection(api, connectionId, updatedJdbcConnection) {
    let response = yield call([api, api.put], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}`, updatedJdbcConnection);
    return response.data;
}

function* deleteJdbcConnection(api, connectionId) {
    yield call([api, api.delete], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}`);
}

function* getJdbcConnectionTables(api, connectionId) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}/tables`);
    return result.data;
}

export function* getJdbcConnectionsRequest(api) {
    try {
        const result = yield call(getJdbcConnections, api);
        yield put(actions.getJdbcConnectionsSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "connections");
        yield put(actions.getJdbcConnectionsFailure(errorObject));
    }
}

export function* createJdbcConnectionsRequest(api, { connection }) {
    try {
        let createdJdbcConnection = yield call(createJdbcConnection, api, connection);
        yield put(actions.createJdbcConnectionSuccess(createdJdbcConnection));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "connection");
        yield put(actions.createJdbcConnectionFailure(errorObject));
    }
}

export function* getJdbcConnectionRequest(api, { connectionId }) {
    try {
        const connection = yield call(getJdbcConnection, api, connectionId);
        yield put(actions.getJdbcConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "system connection");
        yield put(actions.getJdbcConnectionFailure(errorObject));
    }
}

export function* updateJdbcConnectionRequest(api, { connectionId, updatedJdbcConnection }) {
    try {
        const connection = yield call(updateJdbcConnection, api, connectionId, updatedJdbcConnection);
        yield put(actions.updateJdbcConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "connection");
        yield put(actions.updateJdbcConnectionFailure(errorObject));
    }
}

export function* deleteJdbcConnectionRequest(api, { connectionId }) {
    try {
        yield call(deleteJdbcConnection, api, connectionId);
        yield put(actions.deleteJdbcConnectionSuccess(connectionId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "connection");
        yield put(actions.deleteJdbcConnectionFailure(errorObject));
    }
}

export function* getJdbcConnectionTablesRequest(api, { connectionId }) {
    try {
        let tables = yield call(getJdbcConnectionTables, api, connectionId);
        yield put(actions.getJdbcConnectionTablesSuccess(tables));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "JDBC connection tables");
        yield put(actions.getJdbcConnectionTablesFailure(errorObject));
    }
}

export function* previewJdbcTableRequest(api, { params: { connectionId, tableName, limit } }) {
    try {
        const tenant = yield select(getTenant);
        const user = yield select(getUser);
        const finTableName = tableName.Name;
        const response = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}/preview`, { tableName: finTableName, limit });
        yield put(actions.getJdbcTablePreviewSuccess(response.data.data));
    } catch (error) {
        console.log(error);
        const errObj = errorHandler(error, "Preview", "JDBC table");
        yield put(actions.getJdbcTablePreviewFailure(errObj));
    }
}

export function* getJdbcTableSchemaRequest(api, { params: { connectionId, tableName } }) {
    try {

        const connection = yield getJdbcConnection(api, connectionId);

        if (!connection) {
            yield put(actions.getJdbcTableSchemaFailure("Connection not found"));
            return;
        }

        let newTableName
        if(tableName.Type !== undefined) {
            newTableName = tableName.Name
        } else {
            newTableName = tableName;
        }
        const response = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/jdbc/item/${connectionId}/schema`, { tableName: newTableName });

        yield put(actions.getJdbcTableSchemaSuccess(response));
    } catch (error) {
        console.error("Saga: Error in getJdbcTableSchema", error);
        yield put(actions.getJdbcTableSchemaFailure(error.message));
    }
}

export function* watchGetJdbcConnectionsRequest(api, params) {
    yield call(getJdbcConnectionsRequest, api);
}

export function* watchCreateJdbcConnectionRequest(api, { params }) {
    yield call(createJdbcConnectionsRequest, api, params);
}

export function* watchGetJdbcConnectionRequest(api, { params }) {
    yield call(getJdbcConnectionRequest, api, params);
}

export function* watchUpdateJdbcConnectionRequest(api, { params }) {
    yield call(updateJdbcConnectionRequest, api, params);
}

export function* watchDeleteJdbcConnectionRequest(api, { params }) {
    yield call(deleteJdbcConnectionRequest, api, params);
}

export function* watchGetJdbcConnectionTablesRequest(api, { params }) {
    yield call(getJdbcConnectionTablesRequest, api, params);
}

export function* watchGetJdbcTableSchemaRequest(api, { params }) {
    yield call(getJdbcTableSchemaRequest, api, { params });
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_JDBC_CONNECTIONS_REQUEST, watchGetJdbcConnectionsRequest, api);
    yield takeEvery(actions.CREATE_JDBC_CONNECTION_REQUEST, watchCreateJdbcConnectionRequest, api);
    yield takeLatest(actions.GET_JDBC_CONNECTION_REQUEST, watchGetJdbcConnectionRequest, api);
    yield takeLatest(actions.UPDATE_JDBC_CONNECTION_REQUEST, watchUpdateJdbcConnectionRequest, api);
    yield takeLatest(actions.DELETE_JDBC_CONNECTION_REQUEST, watchDeleteJdbcConnectionRequest, api);
    yield takeLatest(actions.GET_JDBC_CONNECTION_TABLES_REQUEST, watchGetJdbcConnectionTablesRequest, api);
    yield takeLatest(actions.GET_JDBC_TABLE_PREVIEW_REQUEST, previewJdbcTableRequest, api);
    yield takeLatest(actions.GET_JDBC_TABLE_SCHEMA_REQUEST, watchGetJdbcTableSchemaRequest, api);
}
